import {
  Box,
  Header,
  SpaceBetween,
  Table,
  Button,
  Modal,
  FormField,
  Input,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  StatusIndicator,
  ExpandableSection,
  Link,
} from '@cloudscape-design/components';

import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { useNavigate } from '@tanstack/react-router';
import {
  useUrlFilterState,
  useUrlRangeState,
  useUrlSortingState,
} from '../../common/filtering/filters';
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocalStorage, useMedia } from 'react-use';
import { Distributor } from '../../common/types';
import {
  getDistributors,
  getDistributorsMeta,
  createDistributor,
  disableDistributor,
  enableDistributor,
} from '../../common/api/distributors';
import { PropertyFilterOption } from '@cloudscape-design/collection-hooks';
import { ResourceError } from '../../components/Loading';
import { TextQueryFilter } from '../../components/TextQueryFilter';
import { PropertyDateFilter } from '../../common/filtering/date-filtering';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { PhoneNumber } from '../../components/PhoneNumber';
import { Email } from '../../components/Email';

// We're already in the scorecard app
const scorecardUrl = '';

function parentName(item: Distributor): string {
  return [item.parent_first_name, item.parent_last_name].filter(Boolean).join(' ') || item.parent_path || '';
}

export default function DistributorsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const resourceName = 'Distributors';
  const endpointName = 'distributors';

  const { query, setQuery } = useUrlFilterState();
  const [textQuery, setTextQuery] = useState('');
  const past = true;
  const { range, setRange } = useUrlRangeState(past);
  const { sortingField, setSortingField, sortDirection, setSortDirection } = useUrlSortingState(
    'created_at',
    'desc',
  );
  const [now, setNow] = useState(new Date());
  const [selectedItems, setSelectedItems] = useState<Distributor[]>([]);
  const isMobile = useMedia('(max-width: 767px)');

  // Modal state for creating distributor
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDistributor, setNewDistributor] = useState({
    path: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    show_company: true,
    show_first_name: true,
    show_last_name: true,
    show_email: true,
    show_phone: true,
    show_additional_info: true,
  });

  const showingDisabled =
    query.tokens.filter((token) => token.propertyKey === 'disabled').length !== 0;

  const DEFAULT_PREFERENCES = {
    pageSize: 50,
  };
  const PAGE_SIZE_OPTIONS = [
    { value: 10, label: `10 ${resourceName}` },
    { value: 20, label: `20 ${resourceName}` },
    { value: 50, label: `50 ${resourceName}` },
    { value: 100, label: `100 ${resourceName}` },
  ];

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps.Preferences<unknown>
  >('React-Distributors-Preferences', DEFAULT_PREFERENCES);

  const pageSize = preferences?.pageSize || DEFAULT_PREFERENCES.pageSize;
  const [page, setPage] = useState(1);

  const metaQuery = useQuery({
    queryKey: [endpointName, 'meta'],
    queryFn: getDistributorsMeta,
    staleTime: 360_000,
    refetchOnWindowFocus: false,
  });

  const { options, properties } = useMemo(() => {
    if (metaQuery.data) {
      return {
        options: metaQuery.data.filteringOptions as PropertyFilterOption[],
        properties: metaQuery.data.filteringProperties,
      };
    }
    return {
      options: [],
      properties: [],
    };
  }, [metaQuery.data]);

  const { isPending, isFetching, error, data } = useQuery({
    queryKey: [
      endpointName,
      query,
      range,
      pageSize,
      sortingField,
      sortDirection,
      page,
      now,
      textQuery,
    ],
    enabled: metaQuery.isSuccess,
    queryFn: async () =>
      await getDistributors(
        query,
        page,
        pageSize,
        range,
        sortingField,
        sortDirection,
        now,
        textQuery,
      ),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const loading =
    (isPending || isFetching || metaQuery.isPending || metaQuery.isFetching) &&
    !error &&
    !metaQuery.error;

  const createMutation = useMutation({
    mutationFn: createDistributor,
    onSuccess: () => {
      setShowCreateModal(false);
      setNewDistributor({
        path: '',
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        show_company: true,
        show_first_name: true,
        show_last_name: true,
        show_email: true,
        show_phone: true,
        show_additional_info: true,
      });
      setNow(new Date());
      queryClient.invalidateQueries({ queryKey: [endpointName] });
    },
  });

  const disableMutation = useMutation({
    mutationFn: async (uuids: string[]) => {
      for (const uuid of uuids) {
        await disableDistributor(uuid);
      }
    },
    onSuccess: () => {
      setSelectedItems([]);
      setNow(new Date());
      queryClient.invalidateQueries({ queryKey: [endpointName] });
    },
  });

  const enableMutation = useMutation({
    mutationFn: async (uuids: string[]) => {
      for (const uuid of uuids) {
        await enableDistributor(uuid);
      }
    },
    onSuccess: () => {
      setSelectedItems([]);
      setQuery({
        operation: query.operation,
        tokens: query.tokens.filter((token) => token.propertyKey !== 'disabled'),
      });
      setNow(new Date());
      queryClient.invalidateQueries({ queryKey: [endpointName] });
    },
  });

  const refresh = () => {
    setNow(new Date());
    metaQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={data?.pages ?? 1}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  return (
    <DashboardViewLayout breadcrumbs={[{ text: resourceName, href: '' }]} contentType="table">
      <Table
        onSortingChange={({ detail }) => {
          setSortingField?.(detail.sortingColumn.sortingField);
          setSortDirection?.(detail.isDescending ? 'desc' : 'asc');
        }}
        sortingColumn={{
          sortingField,
        }}
        sortingDescending={sortDirection === 'desc'}
        header={
          <Header
            variant="awsui-h1-sticky"
            description="Manage distributors who can create and access scorecards."
            actions={
              <SpaceBetween size="xs" direction="horizontal">
                {showingDisabled ? (
                  <Button
                    variant="normal"
                    iconName="undo"
                    onClick={() => enableMutation.mutate(selectedItems.map((item) => item.uuid))}
                    disabled={selectedItems.length === 0 || enableMutation.isPending}
                    loading={enableMutation.isPending}
                  >
                    Enable
                  </Button>
                ) : (
                  <Button
                    variant="normal"
                    iconName="remove"
                    onClick={() => disableMutation.mutate(selectedItems.map((item) => item.uuid))}
                    disabled={selectedItems.length === 0 || disableMutation.isPending}
                    loading={disableMutation.isPending}
                  >
                    Disable
                  </Button>
                )}
                <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
                <Button
                  variant="primary"
                  iconName="add-plus"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Distributor
                </Button>
              </SpaceBetween>
            }
            counter={
              data?.total && metaQuery.data?.total
                ? `(${data.total.toLocaleString()}/${metaQuery.data.total.toLocaleString()})`
                : undefined
            }
          >
            {resourceName}
          </Header>
        }
        items={data?.items ?? []}
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        selectionType="multi"
        loading={loading}
        loadingText={`Loading ${resourceName}...`}
        pagination={pagination}
        preferences={
          <CollectionPreferences
            disabled={loading}
            pageSizePreference={{
              options: PAGE_SIZE_OPTIONS,
            }}
            preferences={preferences}
            onConfirm={(e) => setPreferences(e.detail)}
          />
        }
        filter={
          <SpaceBetween direction="vertical" size="xxxs">
            <TextQueryFilter
              loading={loading}
              text={textQuery ?? ''}
              setText={(q) => {
                setTextQuery?.(q);
                setPage(1);
              }}
            />
            <ExpandableSection headerText="Additional filters">
              <PropertyDateFilter
                loading={loading}
                query={query}
                setQuery={(q) => {
                  setQuery?.(q);
                  setPage(1);
                }}
                range={range ?? null}
                setRange={(r) => {
                  setRange?.(r);
                  setPage(1);
                }}
                propertyFilteringOptions={options}
                filteringProperties={properties}
                past={past}
              />
            </ExpandableSection>
          </SpaceBetween>
        }
        variant="full-page"
        stickyHeader
        empty={
          <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
            {error || metaQuery.error ? (
              <ResourceError
                error={error ?? metaQuery.error ?? new Error('Unknown error')}
                refetch={refresh}
                resourceName={resourceName}
              />
            ) : (
              <SpaceBetween size="xxs">
                <div>
                  <b>No {resourceName.toLowerCase()}</b>
                  <Box color="inherit" variant="p">
                    No {resourceName.toLowerCase()} found.
                  </Box>
                </div>
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  Create Distributor
                </Button>
              </SpaceBetween>
            )}
          </Box>
        }
        columnDefinitions={isMobile ? [
          {
            id: 'distributor',
            header: 'Distributor',
            cell: (item: Distributor) => (
              <SpaceBetween direction="vertical" size="s">
                <SpaceBetween direction="vertical" size="xs">
                  <Button
                    href={`${scorecardUrl}/d/${item.path}`}
                    variant="normal"
                    target="_blank"
                    iconName="transcript"
                    iconAlign="left"
                  >
                    /d/{item.path}
                  </Button>
                  <Button
                    href={`/dashboard/distributors/${item.uuid}`}
                    variant="inline-link"
                    iconName="folder"
                    iconAlign="left"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate({ to: `/dashboard/distributors/${item.uuid}` });
                    }}
                  >
                    View Details
                  </Button>
                  {item.parent_uuid && (
                    <SpaceBetween size="xxxs">
                      <Box color="text-body-secondary">
                        Parent:{' '}
                        <Link
                          href={`/dashboard/distributors/${item.parent_uuid}`}
                          onFollow={(event) => {
                            event.preventDefault();
                            navigate({ to: `/dashboard/distributors/${item.parent_uuid}` });
                          }}
                        >
                          {parentName(item)}
                        </Link>
                      </Box>
                      {item.parent_company && (
                        <Box color="text-body-secondary">{item.parent_company}</Box>
                      )}
                      {item.parent_path && (
                        <Box color="text-body-secondary">/d/{item.parent_path}</Box>
                      )}
                    </SpaceBetween>
                  )}
                </SpaceBetween>
                <SpaceBetween direction="vertical" size="xxxs">
                  <Box variant="strong">{item.first_name} {item.last_name}</Box>
                  {item.company && <Box color="text-body-secondary">{item.company}</Box>}
                  <Email email={item.email} />
                  <PhoneNumber
                    phoneNumber={item.phone_number}
                    lineTypeInfo={item.phone_line_type_info}
                    showLineTypeInfo
                  />
                </SpaceBetween>
                <SpaceBetween direction="vertical" size="xxxs">
                  <Box variant="strong">
                    {item.scorecard_count.toLocaleString()} scorecard{item.scorecard_count === 1 ? '' : 's'}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    {item.last_scorecard_at
                      ? `Latest ${formatDateStringWithTime(item.last_scorecard_at)}`
                      : 'Never'}
                  </Box>
                  {item.disabled ? (
                    <StatusIndicator type="stopped">Disabled</StatusIndicator>
                  ) : (
                    <StatusIndicator type="success">Active</StatusIndicator>
                  )}
                  <Box variant="small" color="text-body-secondary">
                    Created {formatDateStringWithTime(item.created_at)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    Updated {formatDateStringWithTime(item.updated_at)}
                  </Box>
                </SpaceBetween>
              </SpaceBetween>
            ),
          },
        ] : [
          {
            id: 'distributor',
            header: 'Path',
            sortingField: 'path',
            minWidth: 200,
            cell: (item: Distributor) => (
              <SpaceBetween direction="vertical" size="xs">
                <Button
                  href={`${scorecardUrl}/d/${item.path}`}
                  variant="normal"
                  target="_blank"
                  iconName="transcript"
                  iconAlign="left"
                >
                  /d/{item.path}
                </Button>
                <Button
                  href={`/dashboard/distributors/${item.uuid}`}
                  variant="inline-link"
                  iconName="folder"
                  iconAlign="left"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate({ to: `/dashboard/distributors/${item.uuid}` });
                  }}
                >
                  View Details
                </Button>
                {item.parent_uuid && (
                  <Box color="text-body-secondary">
                    Parent:{' '}
                    <Link
                      href={`/dashboard/distributors/${item.parent_uuid}`}
                      onFollow={(event) => {
                        event.preventDefault();
                        navigate({ to: `/dashboard/distributors/${item.parent_uuid}` });
                      }}
                    >
                      {parentName(item)}
                    </Link>
                    {item.parent_company ? ` · ${item.parent_company}` : ''}
                  </Box>
                )}
              </SpaceBetween>
            ),
          },
          {
            id: 'contact',
            header: 'Contact / company',
            cell: (item: Distributor) => (
              <SpaceBetween direction="vertical" size="xxxs">
                <Box variant="strong">
                  {item.first_name} {item.last_name}
                </Box>
                {item.company && <Box color="text-body-secondary">{item.company}</Box>}
                <Email email={item.email} />
                <PhoneNumber
                  phoneNumber={item.phone_number}
                  lineTypeInfo={item.phone_line_type_info}
                  showLineTypeInfo
                />
              </SpaceBetween>
            ),
          },
          {
            id: 'scorecards',
            header: 'Scorecards',
            cell: (item: Distributor) => (
              <SpaceBetween direction="vertical" size="xxxs">
                <Box variant="strong">{item.scorecard_count.toLocaleString()}</Box>
                <Box variant="small" color="text-body-secondary">
                  {item.last_scorecard_at
                    ? `Latest ${formatDateStringWithTime(item.last_scorecard_at)}`
                    : 'Never'}
                </Box>
              </SpaceBetween>
            ),
          },
          {
            id: 'status',
            header: 'Status',
            sortingField: 'created_at',
            cell: (item: Distributor) => (
              <SpaceBetween direction="vertical" size="xxxs">
                {item.disabled ? (
                  <StatusIndicator type="stopped">Disabled</StatusIndicator>
                ) : (
                  <StatusIndicator type="success">Active</StatusIndicator>
                )}
                <Box variant="small" color="text-body-secondary">
                  Created {formatDateStringWithTime(item.created_at)}
                </Box>
                <Box variant="small" color="text-body-secondary">
                  Updated {formatDateStringWithTime(item.updated_at)}
                </Box>
              </SpaceBetween>
            ),
          },
        ]}
      />

      {/* Create Distributor Modal */}
      <Modal
        visible={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        header="Create Distributor"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => createMutation.mutate(newDistributor)}
                loading={createMutation.isPending}
                disabled={
                  !newDistributor.path ||
                  !newDistributor.first_name ||
                  !newDistributor.last_name ||
                  !newDistributor.email
                }
              >
                Create
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField
            label="Custom Path"
            description={`${scorecardUrl || window.location.origin}/d/${newDistributor.path || '<custom_path>'}`}
            constraintText="Lowercase letters, numbers, and hyphens only"
          >
            <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
              <Box variant="code">/d/</Box>
              <Input
                value={newDistributor.path}
                onChange={({ detail }) =>
                  setNewDistributor({
                    ...newDistributor,
                    path: detail.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                  })
                }
                placeholder="atlanta"
              />
            </SpaceBetween>
          </FormField>
          <FormField label="First Name">
            <Input
              value={newDistributor.first_name}
              onChange={({ detail }) =>
                setNewDistributor({ ...newDistributor, first_name: detail.value })
              }
              placeholder="John"
            />
          </FormField>
          <FormField label="Last Name">
            <Input
              value={newDistributor.last_name}
              onChange={({ detail }) =>
                setNewDistributor({ ...newDistributor, last_name: detail.value })
              }
              placeholder="Doe"
            />
          </FormField>
          <FormField label="Email">
            <Input
              type="email"
              value={newDistributor.email}
              onChange={({ detail }) =>
                setNewDistributor({ ...newDistributor, email: detail.value })
              }
              placeholder="john@example.com"
            />
          </FormField>
          <FormField label="Phone Number" description="Optional">
            <Input
              value={newDistributor.phone_number}
              onChange={({ detail }) =>
                setNewDistributor({ ...newDistributor, phone_number: detail.value })
              }
              placeholder="+1 555 123 4567"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </DashboardViewLayout>
  );
}
